import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createUserSchema } from '@core/schemas/users';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useCreateUser } from '@/hooks/users';
import axios from 'axios';

type CreateFormData = z.input<typeof createUserSchema>;

export function CreateUserForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { role: 'AGENT' },
  });

  const mutation = useCreateUser();

  const onSubmit = (data: CreateFormData) =>
    mutation.mutate(data, {
      onSuccess: onCreated,
      onError: (err) => {
        const message = axios.isAxiosError(err)
          ? (err.response?.data?.error ?? 'Failed to create user')
          : 'Failed to create user';
        setError('root', { message });
      },
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add User</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4' noValidate>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='create-name'>Name</Label>
              <Input id='create-name' {...register('name')} aria-invalid={!!errors.name} />
              {errors.name && <p className='text-destructive text-xs'>{errors.name.message}</p>}
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='create-email'>Email</Label>
              <Input id='create-email' type='email' autoComplete='off' {...register('email')} aria-invalid={!!errors.email} />
              {errors.email && <p className='text-destructive text-xs'>{errors.email.message}</p>}
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='create-password'>Password</Label>
              <Input id='create-password' type='password' autoComplete='new-password' {...register('password')} aria-invalid={!!errors.password} />
              {errors.password && <p className='text-destructive text-xs'>{errors.password.message}</p>}
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='create-role'>Role</Label>
              <select
                id='create-role'
                {...register('role')}
                className='border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
              >
                <option value='AGENT'>Agent</option>
                <option value='ADMIN'>Admin</option>
              </select>
            </div>
          </div>
          {errors.root && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}
          <div className='flex gap-2 justify-end'>
            <Button type='button' variant='outline' onClick={onCancel}>
              Cancel
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
